import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'
import { journalLoanReceived, journalLoanRepayment, journalInterestAccrual, journalInterestPayment } from '../lib/journalEngine'

const LENDER_TYPES = ['Bank', 'Individual', 'Director', 'Family', 'Other']
const INTEREST_FREQ = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi-annual', label: 'Semi-Annual (every 6 months)' },
  { value: 'annual', label: 'Annual' },
]

const emptyLoan = {
  lender_name: '', lender_type: 'Individual', account_code: '2040',
  original_amount: '', currency: 'UGX', interest_rate: 0,
  interest_frequency: 'monthly',
  start_date: ugandaDate(),
  maturity_date: '', repayment_terms: '', monthly_payment: 0, notes: ''
}

export default function Loans() {
  const { user, isAdmin } = useAuth()
  const [loans, setLoans] = useState([])
  const [repayments, setRepayments] = useState([])
  const [interestAccruals, setInterestAccruals] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [payModal, setPayModal] = useState(null)
  const [loanModal, setLoanModal] = useState(null)
  const [accrualModal, setAccrualModal] = useState(null) // for viewing accruals
  const [payForm, setPayForm] = useState({
    amount: '', payment_date: ugandaDate(),
    includes_interest: true, interest_amount: '', reference: '', notes: ''
  })

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    setLoading(true)
    const [loanRes, repRes, accrualRes] = await Promise.all([
      supabase.from('loans').select('*').order('created_at'),
      supabase.from('loan_repayments').select('*').order('payment_date', { ascending: false }),
      supabase.from('interest_accruals').select('*').order('accrual_date', { ascending: false }),
    ])
    setLoans(loanRes.data || [])
    setRepayments(repRes.data || [])
    setInterestAccruals(accrualRes.data || [])
    setLoading(false)
  }

  // Calculate next interest due date and amount for a loan
  const getNextInterestDue = (loan) => {
    if (!loan.interest_rate || loan.interest_rate <= 0) return null
    const freq = loan.interest_frequency || 'monthly'
    const freqDays = { monthly: 30, quarterly: 91, 'semi-annual': 182, annual: 365 }
    const loanAccruals = interestAccruals.filter(a => a.loan_id === loan.id)
    let nextDue
    if (loanAccruals.length === 0) {
      // First accrual due one period after start date
      nextDue = new Date(loan.start_date)
    } else {
      const lastAccrual = new Date(loanAccruals[0].accrual_date)
      nextDue = new Date(lastAccrual)
    }
    // Add frequency period
    if (freq === 'monthly') nextDue.setMonth(nextDue.getMonth() + 1)
    else if (freq === 'quarterly') nextDue.setMonth(nextDue.getMonth() + 3)
    else if (freq === 'semi-annual') nextDue.setMonth(nextDue.getMonth() + 6)
    else nextDue.setFullYear(nextDue.getFullYear() + 1)

    const freqMonths = { monthly: 1, quarterly: 3, 'semi-annual': 6, annual: 12 }
    const months = freqMonths[freq] || 1
    const interestAmount = Math.round(loan.outstanding_balance * loan.interest_rate / 100 / 12 * months)
    const today = new Date()
    const daysUntilDue = Math.floor((nextDue - today) / 86400000)
    return { date: nextDue, amount: interestAmount, daysUntilDue, overdue: daysUntilDue < 0, dueSoon: daysUntilDue >= 0 && daysUntilDue <= 7, freq }
  }

  // Accrue interest — creates an accrual provision but NOT a cash movement
  const handleAccrueInterest = async (loan) => {
    const next = getNextInterestDue(loan)
    if (!next) return
    setSaving(true)
    // Create interest accrual record (liability, not cash)
    await supabase.from('interest_accruals').insert({
      loan_id: loan.id,
      loan_name: loan.lender_name,
      accrual_date: next.date.toLocaleDateString('en-CA', { timeZone: 'Africa/Kampala' }),
      interest_amount: next.amount,
      frequency: next.freq,
      status: 'Accrued', // 'Accrued' | 'Paid'
      user_id: user.id,
    })
    // Also create an accrual provision in WeeklyPL so it shows on P&L
    await supabase.from('accrual_provisions').insert({
      user_id: user.id,
      period_month: next.date.toLocaleDateString('en-CA', { timeZone: 'Africa/Kampala' }).slice(0, 7),
      category: 'Interest Expense',
      description: `Interest on loan — ${loan.lender_name}`,
      estimated_amount: next.amount,
      is_reconciled: false,
    })
    // Send email notification to Edward and Sharon
    try {
      await fetch('/api/notify-interest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lender_name: loan.lender_name,
          interest_amount: next.amount,
          due_date: next.date.toLocaleDateString('en-CA', { timeZone: 'Africa/Kampala' }),
          outstanding_balance: loan.outstanding_balance,
          frequency: next.freq,
        })
      })
    } catch (e) { /* email failure shouldn't block the accrual */ }
    setMsg({ type: 'success', text: `Interest of UGX ${next.amount.toLocaleString()} accrued for ${loan.lender_name}. This is a liability — it will affect cash only when you record the payment.` })
    setSaving(false); loadData()
    setTimeout(() => setMsg(null), 5000)
  }

  const handleSaveLoan = async () => {
    if (!loanModal.lender_name || !loanModal.original_amount) {
      setMsg({ type: 'error', text: 'Lender name and amount are required.' }); return
    }
    setSaving(true)
    const amount = parseFloat(loanModal.original_amount)
    const { id, ...data } = loanModal
    const payload = {
      ...data,
      original_amount: amount,
      outstanding_balance: id ? loanModal.outstanding_balance : amount,
      total_paid: id ? loanModal.total_paid : 0,
      monthly_payment: parseFloat(loanModal.monthly_payment) || 0,
      interest_rate: parseFloat(loanModal.interest_rate) || 0,
      interest_frequency: loanModal.interest_frequency || 'monthly',
      loan_number: id ? loanModal.loan_number : `LOAN-${Date.now().toString().slice(-4)}`,
      created_by: user.id
    }
    const { error } = id
      ? await supabase.from('loans').update(payload).eq('id', id)
      : await supabase.from('loans').insert(payload)
    if (error) { setMsg({ type: 'error', text: error.message }); setSaving(false); return }

    if (!id) {
      // New loan — auto cash log and journal entry
      await supabase.from('cash_logs').insert({
        user_id: user.id,
        log_date: loanModal.start_date || ugandaDate(),
        total_income: amount, total_expenses: 0, net: amount,
        income_rows: [{ type: 'loan', description: `Loan received — ${loanModal.lender_name}`, total: amount }],
        expense_rows: [],
        notes: `Loan received from ${loanModal.lender_name}. Ref: ${payload.loan_number}`,
      })
      const { data: newLoan } = await supabase.from('loans').select('id').eq('loan_number', payload.loan_number).single()
      if (newLoan) {
        await journalLoanReceived({
          loanId: newLoan.id, lenderName: loanModal.lender_name, amount,
          date: loanModal.start_date || ugandaDate(),
          userId: user.id,
        })
      }
    } else {
      // Edit — detect if additional funds were drawn down
      const { data: existing } = await supabase.from('loans').select('original_amount').eq('id', id).single()
      const prevAmount = parseFloat(existing?.original_amount || 0)
      const additionalDrawdown = amount - prevAmount
      if (additionalDrawdown > 0) {
        const drawDate = ugandaDate()
        await supabase.from('cash_logs').insert({
          user_id: user.id,
          log_date: drawDate,
          total_income: additionalDrawdown, total_expenses: 0, net: additionalDrawdown,
          income_rows: [{ type: 'loan', description: `Additional loan drawdown — ${loanModal.lender_name}`, total: additionalDrawdown }],
          expense_rows: [],
          notes: `Additional drawdown from ${loanModal.lender_name}.`,
        })
        await supabase.from('journal_entries').insert({
          journal_id: `JNL-LOAN-ADD-${id}-${Date.now()}`,
          entry_date: drawDate,
          module: 'loan',
          debit_account: '1101',
          credit_account: '2201',
          amount: additionalDrawdown,
          description: `Additional drawdown — ${loanModal.lender_name}`,
          source_id: id,
          created_by: user.id,
        })
      }
    }
    setMsg({ type: 'success', text: id ? 'Loan updated.' : `Loan added. UGX ${amount.toLocaleString()} added to cash balance.` })
    setLoanModal(null); loadData()
    setSaving(false); setTimeout(() => setMsg(null), 3000)
  }

  const handleRepayment = async () => {
    if (!payForm.amount) return
    setSaving(true)
    const amount = parseFloat(payForm.amount)
    const interestPaid = payForm.includes_interest ? (parseFloat(payForm.interest_amount) || 0) : 0
    const principal = amount - interestPaid
    const { error } = await supabase.from('loan_repayments').insert({
      loan_id: payModal.id, payment_date: payForm.payment_date,
      amount, principal, interest: interestPaid,
      reference: payForm.reference, notes: payForm.notes,
      recorded_by: user.id
    })
    if (!error) {
      // Update outstanding balance
      await supabase.from('loans').update({
        outstanding_balance: Math.max(0, (payModal.outstanding_balance || 0) - principal),
        total_paid: (payModal.total_paid || 0) + amount,
        status: ((payModal.outstanding_balance || 0) - principal) <= 0 ? 'Paid Off' : 'Active',
      }).eq('id', payModal.id)

      // Auto cash log (cash out)
      await supabase.from('cash_logs').insert({
        user_id: user.id,
        log_date: payForm.payment_date,
        total_income: 0, total_expenses: amount, net: -amount,
        income_rows: [],
        expense_rows: [{ type: 'loan', description: `Loan repayment — ${payModal.lender_name}${interestPaid > 0 ? ` (incl. UGX ${interestPaid.toLocaleString()} interest)` : ''}`, amount }],
        notes: `Loan repayment to ${payModal.lender_name}. ${payForm.notes || ''}`,
      })

      // Journal entries for repayment
      const { data: savedRepayment } = await supabase.from('loan_repayments')
        .select('id').eq('loan_id', payModal.id).order('created_at', { ascending: false }).limit(1).single()
      if (savedRepayment) {
        if (principal > 0) {
          // Dr 2201 Loan Payable / Cr 1101 Cash (principal)
          await journalLoanRepayment({
            repaymentId: savedRepayment.id, loanId: payModal.id,
            lenderName: payModal.lender_name, principal,
            date: payForm.payment_date, userId: user.id,
          })
        }
        if (interestPaid > 0) {
          // Dr 2401 Accrued Interest / Cr 1101 Cash (interest)
          await journalInterestPayment({
            repaymentId: savedRepayment.id, lenderName: payModal.lender_name,
            interest: interestPaid, date: payForm.payment_date, userId: user.id,
          })
        }
      }

      // If interest included, mark related accruals as paid
      if (interestPaid > 0) {
        const unpaidAccruals = interestAccruals
          .filter(a => a.loan_id === payModal.id && a.status === 'Accrued')
          .slice(0, 1) // mark the oldest unpaid one
        if (unpaidAccruals.length > 0) {
          await supabase.from('interest_accruals').update({ status: 'Paid', paid_on: payForm.payment_date }).eq('id', unpaidAccruals[0].id)
          // Reconcile the provision
          const { data: prov } = await supabase.from('accrual_provisions')
            .select('id').eq('category', 'Interest Expense').eq('is_reconciled', false)
            .ilike('description', `%${payModal.lender_name}%`).limit(1)
          if (prov?.[0]) {
            await supabase.from('accrual_provisions').update({
              actual_amount: interestPaid, is_reconciled: true, reconciled_at: new Date().toISOString()
            }).eq('id', prov[0].id)
          }
        }
      }

      setMsg({ type: 'success', text: `Repayment of UGX ${amount.toLocaleString()} recorded. Cash balance updated.` })
      setPayModal(null)
      setPayForm({ amount: '', payment_date: ugandaDate(), includes_interest: true, interest_amount: '', reference: '', notes: '' })
      loadData()
    } else { setMsg({ type: 'error', text: error.message }) }
    setSaving(false); setTimeout(() => setMsg(null), 3000)
  }

  const totalBorrowed = loans.reduce((s, l) => s + (l.original_amount || 0), 0)
  const totalOutstanding = loans.reduce((s, l) => s + (l.outstanding_balance || 0), 0)
  const totalRepaid = loans.reduce((s, l) => s + (l.total_paid || 0), 0)
  const monthlyInterest = loans.filter(l => l.status === 'Active' && l.interest_rate > 0)
    .reduce((s, l) => s + (l.outstanding_balance * l.interest_rate / 100 / 12), 0)
  const unpaidAccruedInterest = interestAccruals.filter(a => a.status === 'Accrued')
    .reduce((s, a) => s + (a.interest_amount || 0), 0)

  return (
    <AppLayout title="Loans & Liabilities">
      <div className="section-header">
        <div className="section-icon">🏦</div>
        <div className="section-info">
          <h2>Loans &amp; Liabilities</h2>
          <p>Track borrowings and interest. Interest accrues as a liability — only affects cash when paid.</p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={() => setLoanModal({ ...emptyLoan })}>+ Add Loan</button>
        )}
      </div>

      {msg && <div className={`alert ${msg.type === 'success' ? 'alert-green' : 'alert-red'}`}>{msg.text}</div>}

      {/* KPIs */}
      <div className="kpi-grid" style={{ marginBottom: 18 }}>
        <div className="kpi"><div className="kpi-label">Total Borrowed</div><div className="kpi-value" style={{ fontSize:'1.2rem' }}>UGX {totalBorrowed.toLocaleString()}</div></div>
        <div className="kpi red"><div className="kpi-label">Still Outstanding</div><div className="kpi-value" style={{ fontSize:'1.2rem' }}>UGX {totalOutstanding.toLocaleString()}</div></div>
        <div className="kpi green"><div className="kpi-label">Total Repaid</div><div className="kpi-value" style={{ fontSize:'1.2rem' }}>UGX {totalRepaid.toLocaleString()}</div></div>
        <div className={`kpi ${unpaidAccruedInterest > 0 ? 'red' : ''}`}>
          <div className="kpi-label">Accrued Interest Unpaid</div>
          <div className="kpi-value" style={{ fontSize:'1.1rem' }}>{unpaidAccruedInterest > 0 ? `UGX ${Math.round(unpaidAccruedInterest).toLocaleString()}` : 'UGX 0'}</div>
          <div className="kpi-note">{monthlyInterest > 0 ? `UGX ${Math.round(monthlyInterest).toLocaleString()}/month running` : 'No interest-bearing loans'}</div>
        </div>
      </div>

      {loading ? <div style={{ textAlign:'center', padding:40 }}><span className="spinner dark" /></div>
      : loans.length === 0 ? (
        <div className="empty-state"><div className="empty-icon">🏦</div><h3>No loans recorded</h3><p>Add a loan to start tracking your liabilities.</p></div>
      ) : loans.map(loan => {
        const paidPct = loan.original_amount > 0 ? (loan.total_paid / loan.original_amount * 100) : 0
        const loanReps = repayments.filter(r => r.loan_id === loan.id)
        const loanAccruals = interestAccruals.filter(a => a.loan_id === loan.id)
        const nextInterest = loan.interest_rate > 0 ? getNextInterestDue(loan) : null
        const unpaidAccruals = loanAccruals.filter(a => a.status === 'Accrued')

        return (
          <div key={loan.id} className="card" style={{ marginBottom: 16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
              <div>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.2rem', fontWeight:700, color:'var(--green-deep)', marginBottom:2 }}>{loan.lender_name}</div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:4 }}>
                  <span className="badge badge-grey">{loan.lender_type}</span>
                  <span className="badge badge-grey">{loan.currency}</span>
                  {loan.interest_rate > 0
                    ? <span className="badge badge-red">{loan.interest_rate}% p.a. · {INTEREST_FREQ.find(f=>f.value===loan.interest_frequency)?.label || 'Monthly'}</span>
                    : <span className="badge badge-green">Interest Free</span>}
                </div>
                <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>
                  Started {new Date(loan.start_date).toLocaleDateString('en-UG')}
                  {loan.maturity_date ? ` · Due ${new Date(loan.maturity_date).toLocaleDateString('en-UG')}` : ''}
                  {loan.monthly_payment > 0 ? ` · UGX ${loan.monthly_payment.toLocaleString()}/payment` : ''}
                </div>
                {loan.repayment_terms && <div style={{ fontSize:'0.75rem', color:'var(--amber)', marginTop:3, fontStyle:'italic' }}>{loan.repayment_terms}</div>}
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <span className={`badge ${loan.status==='Active'?'badge-amber':loan.status==='Paid Off'?'badge-green':'badge-red'}`}>{loan.status}</span>
                {isAdmin && <button className="btn btn-ghost btn-sm" onClick={() => setLoanModal(loan)}>✏️ Edit</button>}
              </div>
            </div>

            {/* Progress bar */}
            <div style={{ marginBottom:14 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.75rem', color:'var(--text-muted)', marginBottom:6 }}>
                <span>Repaid: UGX {(loan.total_paid||0).toLocaleString()} ({paidPct.toFixed(1)}%)</span>
                <span style={{ color:'var(--red)', fontWeight:600 }}>Outstanding: UGX {(loan.outstanding_balance||0).toLocaleString()}</span>
              </div>
              <div style={{ height:8, background:'var(--border)', borderRadius:4, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${Math.min(paidPct,100)}%`, background:'var(--green-bright)', borderRadius:4 }} />
              </div>
            </div>

            <div className="grid-3" style={{ marginBottom:14 }}>
              {[
                ['Original Amount', `UGX ${(loan.original_amount||0).toLocaleString()}`, 'var(--text)', 'var(--cream)'],
                ['Total Repaid', `UGX ${(loan.total_paid||0).toLocaleString()}`, 'var(--green-mid)', 'var(--green-pale)'],
                ['Still Owed', `UGX ${(loan.outstanding_balance||0).toLocaleString()}`, 'var(--red)', 'var(--red-pale)'],
              ].map(([label, val, color, bg]) => (
                <div key={label} style={{ textAlign:'center', padding:12, background:bg, borderRadius:8 }}>
                  <div style={{ fontSize:'0.65rem', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', color:'var(--text-muted)', marginBottom:4 }}>{label}</div>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem', fontWeight:700, color }}>{val}</div>
                </div>
              ))}
            </div>

            {/* Interest accrual section */}
            {loan.interest_rate > 0 && loan.status === 'Active' && (
              <div style={{ background:'var(--amber-pale)', border:'1px solid var(--amber-light)', borderRadius:8, padding:14, marginBottom:14 }}>
                <div style={{ fontWeight:700, fontSize:'0.82rem', marginBottom:8, color:'var(--amber)' }}>💸 Interest Accrual</div>

                {/* Next interest due */}
                {nextInterest && (
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
                    <div>
                      <div style={{ fontSize:'0.78rem' }}>
                        <strong>Next interest due:</strong> {nextInterest.date.toLocaleDateString('en-UG')} —
                        <strong style={{ color:'var(--red)' }}> UGX {nextInterest.amount.toLocaleString()}</strong>
                      </div>
                      <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:2 }}>
                        {nextInterest.overdue
                          ? `⚠️ OVERDUE by ${Math.abs(nextInterest.daysUntilDue)} days — will auto-accrue tonight`
                          : nextInterest.dueSoon
                            ? `⏰ Due in ${nextInterest.daysUntilDue} days — will auto-accrue on due date`
                            : `Due in ${nextInterest.daysUntilDue} days`}
                        {unpaidAccruals.length > 0 && ` · ${unpaidAccruals.length} unpaid accrual(s) — UGX ${unpaidAccruals.reduce((s,a)=>s+(a.interest_amount||0),0).toLocaleString()}`}
                      </div>
                    </div>
                    <span style={{ fontSize:'0.7rem', background:'var(--green-pale)', color:'var(--green-mid)', padding:'4px 10px', borderRadius:20, fontWeight:700 }}>🤖 Auto-accrues daily</span>
                  </div>
                )}

                {/* Accrual history */}
                {loanAccruals.length > 0 && (
                  <div style={{ marginTop:10 }}>
                    <div style={{ fontSize:'0.7rem', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', color:'var(--text-muted)', marginBottom:6 }}>Accrual History</div>
                    <div className="table-wrap">
                      <table>
                        <thead><tr><th>Period</th><th>Amount</th><th>Status</th><th>Paid On</th></tr></thead>
                        <tbody>
                          {loanAccruals.map(a => (
                            <tr key={a.id}>
                              <td style={{ fontSize:'0.75rem' }}>{new Date(a.accrual_date).toLocaleDateString('en-UG')}</td>
                              <td style={{ fontFamily:"'DM Mono',monospace", fontWeight:600 }}>UGX {(a.interest_amount||0).toLocaleString()}</td>
                              <td><span className={`badge ${a.status==='Paid'?'badge-green':'badge-amber'}`}>{a.status}</span></td>
                              <td style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{a.paid_on ? new Date(a.paid_on).toLocaleDateString('en-UG') : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div style={{ marginTop:8, fontSize:'0.72rem', color:'var(--text-muted)' }}>
                  🤖 Interest accrues <strong>automatically every morning</strong> when due — recorded as a liability and P&L expense. You and Sharon receive an email notification. Cash is only affected when you record a repayment.
                </div>
              </div>
            )}

            {/* Repayment history */}
            {loanReps.length > 0 && (
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:'0.72rem', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.1em', color:'var(--text-muted)', marginBottom:8 }}>Repayment History</div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Date</th><th>Principal</th><th>Interest</th><th>Total</th><th>Reference</th></tr></thead>
                    <tbody>
                      {loanReps.map(r => (
                        <tr key={r.id}>
                          <td style={{ fontSize:'0.78rem' }}>{new Date(r.payment_date).toLocaleDateString('en-UG')}</td>
                          <td style={{ color:'var(--green-mid)', fontWeight:600 }}>UGX {(r.principal||0).toLocaleString()}</td>
                          <td style={{ color:'var(--red)' }}>{r.interest > 0 ? `UGX ${r.interest.toLocaleString()}` : '—'}</td>
                          <td style={{ fontWeight:700 }}>UGX {(r.amount||0).toLocaleString()}</td>
                          <td style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{r.reference || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {isAdmin && loan.status === 'Active' && (
              <button className="btn btn-primary btn-sm" onClick={() => {
                const nextInt = loan.interest_rate > 0 ? getNextInterestDue(loan) : null
                const unpaid = interestAccruals.filter(a => a.loan_id === loan.id && a.status === 'Accrued')
                const totalUnpaidInterest = unpaid.reduce((s,a) => s+(a.interest_amount||0), 0)
                setPayModal(loan)
                setPayForm({
                  amount: loan.monthly_payment || '',
                  payment_date: ugandaDate(),
                  includes_interest: loan.interest_rate > 0,
                  interest_amount: totalUnpaidInterest || (nextInt?.amount || ''),
                  reference: '', notes: ''
                })
              }}>💳 Record Repayment</button>
            )}

            {loan.notes && <div style={{ marginTop:10, fontSize:'0.75rem', color:'var(--text-muted)', fontStyle:'italic', borderTop:'1px solid var(--border)', paddingTop:8 }}>{loan.notes}</div>}
          </div>
        )
      })}

      {/* Add/Edit Loan Modal */}
      {loanModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setLoanModal(null)}>
          <div className="modal" style={{ maxWidth:580 }}>
            <div className="modal-title">{loanModal.id ? 'Edit Loan' : '+ Add New Loan'}</div>
            <div className="modal-subtitle">All loans are recorded as liabilities on the balance sheet.</div>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Lender Name *</label><input className="form-input" placeholder="e.g. Stanbic Bank" value={loanModal.lender_name} onChange={e => setLoanModal(l=>({...l,lender_name:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Lender Type</label>
                <select className="form-select" value={loanModal.lender_type} onChange={e => setLoanModal(l=>({...l,lender_type:e.target.value}))}>
                  {LENDER_TYPES.map(t=><option key={t}>{t}</option>)}
                </select></div>
              <div className="form-group"><label className="form-label">Original Amount (UGX) *</label><input className="form-input" type="number" placeholder="0" value={loanModal.original_amount} onChange={e => setLoanModal(l=>({...l,original_amount:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Currency</label>
                <select className="form-select" value={loanModal.currency} onChange={e => setLoanModal(l=>({...l,currency:e.target.value}))}>
                  <option>UGX</option><option>USD</option><option>EUR</option><option>GBP</option>
                </select></div>
              <div className="form-group"><label className="form-label">Interest Rate (% per year) — 0 if interest-free</label>
                <input className="form-input" type="number" step="0.1" placeholder="0" value={loanModal.interest_rate}
                  onChange={e => setLoanModal(l=>({...l, interest_rate:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Interest Charged</label>
                <select className="form-select" value={loanModal.interest_frequency || 'monthly'}
                  onChange={e => setLoanModal(l=>({...l,interest_frequency:e.target.value}))}
                  disabled={!parseFloat(loanModal.interest_rate)}>
                  {INTEREST_FREQ.map(f=><option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
                {!parseFloat(loanModal.interest_rate) && <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', marginTop:3 }}>Set an interest rate to enable frequency</div>}
              </div>
              <div className="form-group"><label className="form-label">Regular Repayment Amount (UGX)</label><input className="form-input" type="number" placeholder="0 if flexible" value={loanModal.monthly_payment} onChange={e => setLoanModal(l=>({...l,monthly_payment:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Start Date</label><input className="form-input" type="date" value={loanModal.start_date} onChange={e => setLoanModal(l=>({...l,start_date:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Maturity Date (if any)</label><input className="form-input" type="date" value={loanModal.maturity_date||''} onChange={e => setLoanModal(l=>({...l,maturity_date:e.target.value}))} /></div>
            </div>
            <div className="form-group"><label className="form-label">Repayment Terms</label><input className="form-input" placeholder="e.g. Monthly instalments of UGX 500,000" value={loanModal.repayment_terms||''} onChange={e => setLoanModal(l=>({...l,repayment_terms:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" style={{ minHeight:60 }} value={loanModal.notes||''} onChange={e => setLoanModal(l=>({...l,notes:e.target.value}))} /></div>
            {loanModal.original_amount && parseFloat(loanModal.interest_rate) > 0 && (() => {
              const freq = loanModal.interest_frequency || 'monthly'
              const months = { monthly:1, quarterly:3, 'semi-annual':6, annual:12 }[freq] || 1
              const interest = Math.round(parseFloat(loanModal.original_amount) * parseFloat(loanModal.interest_rate) / 100 / 12 * months)
              return (
                <div style={{ background:'var(--amber-pale)', borderRadius:8, padding:12, marginBottom:12, fontSize:'0.82rem' }}>
                  <strong>Interest per {freq} period:</strong> UGX {interest.toLocaleString()}
                  <span style={{ color:'var(--text-muted)', marginLeft:8 }}>— recorded as accrual (not cash) until paid</span>
                </div>
              )
            })()}
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setLoanModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveLoan} disabled={saving}>
                {saving ? <><span className="spinner"/>Saving...</> : '💾 Save Loan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Repayment Modal */}
      {payModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setPayModal(null)}>
          <div className="modal">
            <div className="modal-title">💳 Record Loan Repayment</div>
            <div className="modal-subtitle">
              <strong>{payModal.lender_name}</strong><br/>
              Outstanding: UGX {(payModal.outstanding_balance||0).toLocaleString()}
              {payModal.interest_rate > 0 && (() => {
                const unpaid = interestAccruals.filter(a=>a.loan_id===payModal.id&&a.status==='Accrued')
                const totalUnpaid = unpaid.reduce((s,a)=>s+(a.interest_amount||0),0)
                return totalUnpaid > 0 ? <span style={{ color:'var(--red)' }}> · Unpaid interest: UGX {totalUnpaid.toLocaleString()}</span> : null
              })()}
            </div>
            {payModal.interest_rate > 0 && (
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'var(--amber-pale)', borderRadius:8, marginBottom:12 }}>
                <input type="checkbox" checked={payForm.includes_interest} onChange={e=>setPayForm(f=>({...f,includes_interest:e.target.checked}))} id="incl-int" />
                <label htmlFor="incl-int" style={{ fontSize:'0.82rem', fontWeight:600, cursor:'pointer' }}>This payment includes interest</label>
              </div>
            )}
            {payModal.interest_rate > 0 && payForm.includes_interest && (
              <div className="form-group">
                <label className="form-label">Interest Amount Included (UGX)</label>
                <input className="form-input" type="number" value={payForm.interest_amount} onChange={e=>setPayForm(f=>({...f,interest_amount:e.target.value}))} />
                <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', marginTop:3 }}>This will mark accrued interest as paid and reconcile the P&L provision</div>
              </div>
            )}
            <div className="form-group"><label className="form-label">Total Amount Paid (UGX) *</label><input className="form-input" type="number" value={payForm.amount} onChange={e=>setPayForm(f=>({...f,amount:e.target.value}))} autoFocus /></div>
            {payForm.amount && payModal.interest_rate > 0 && payForm.includes_interest && (
              <div style={{ background:'var(--cream)', borderRadius:8, padding:10, marginBottom:12, fontSize:'0.82rem' }}>
                <div>Interest: UGX {parseFloat(payForm.interest_amount||0).toLocaleString()}</div>
                <div>Principal: UGX {Math.max(0, parseFloat(payForm.amount||0) - parseFloat(payForm.interest_amount||0)).toLocaleString()}</div>
                <div style={{ fontWeight:700, marginTop:4 }}>New outstanding: UGX {Math.max(0, (payModal.outstanding_balance||0) - Math.max(0, parseFloat(payForm.amount||0) - parseFloat(payForm.interest_amount||0))).toLocaleString()}</div>
              </div>
            )}
            <div className="form-group"><label className="form-label">Payment Date</label><input className="form-input" type="date" value={payForm.payment_date} onChange={e=>setPayForm(f=>({...f,payment_date:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Bank Reference / Transaction ID</label><input className="form-input" placeholder="Bank transaction ID" value={payForm.reference} onChange={e=>setPayForm(f=>({...f,reference:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Notes</label><input className="form-input" value={payForm.notes} onChange={e=>setPayForm(f=>({...f,notes:e.target.value}))} /></div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setPayModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleRepayment} disabled={saving||!payForm.amount}>
                {saving ? <><span className="spinner"/>Saving...</> : '💳 Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
